import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useFlowstackOptional } from '@flowstack/sdk';
import WorkspaceLayout from './layouts/WorkspaceLayout';
import Landing from './pages/Landing';
import AuthModal from '@/components/auth/AuthModal';
import { WEB_BUILDER_ENABLED } from './lib/flags';

/**
 * P0-127: Rescue-from-OAuth-redirect for the built-app login broker.
 *
 * When a user logs in via Privy OAuth (Google/Apple/Twitter) from a built
 * app's popup, Privy's OAuth callback redirects the popup to Casino's root
 * instead of back to /auth/broker?return=... — the query params are lost and
 * the user lands on ComingSoon inside the popup.
 *
 * AuthBrokerPage now persists the original params to sessionStorage. This
 * hook checks on every route change: if sessionStorage has fresh broker params
 * AND we're not already on /auth/broker, navigate back there so the broker can
 * complete the Privy → JWT → hand-off flow.
 *
 * Two transports land here differently:
 *   - postMessage mode (desktop built-app popup): has a window.opener to post
 *     credentials back to — require it.
 *   - redirect mode (mobile native via expo-web-browser's
 *     ASWebAuthenticationSession): a TOP-LEVEL browser context with NO
 *     window.opener; it completes by navigating to an allowlisted deep link.
 *     Gating this path on window.opener stranded the user on Casino's landing
 *     page after a Google/Apple OAuth round-trip returned to '/' — "logged in
 *     but never redirected back to the app". So only require an opener for the
 *     popup path.
 */
function BrokerRescue() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.pathname === '/auth/broker') return;
    if (typeof window === 'undefined') return;
    try {
      const raw = sessionStorage.getItem('flowstack-broker-params');
      if (!raw) return;
      const d = JSON.parse(raw);
      if (!d?.ret || !d?.st) return;
      if (Date.now() - d.ts > 10 * 60 * 1000) {
        sessionStorage.removeItem('flowstack-broker-params');
        return;
      }
      // Popup (postMessage) needs an opener; mobile redirect mode does not.
      const isRedirect = d.mode === 'redirect';
      if (!isRedirect && (!window.opener || window.opener.closed)) return;
      navigate('/auth/broker', { replace: true });
    } catch { /* noop */ }
  }, [location.pathname, navigate]);
  return null;
}

/**
 * Wrap lazy() so that if a chunk fails to load after a new deploy — which
 * manifests as `'text/html' is not a valid JavaScript MIME type` when
 * CloudFront serves the SPA fallback for a missing chunk — we force a
 * one-shot full reload instead of leaving the user stuck on a blank page.
 *
 * We guard against reload loops with a sessionStorage flag.
 */
function lazyWithReload<T extends { default: any }>(factory: () => Promise<T>) {
  return lazy(() =>
    factory().catch((err) => {
      const msg = String(err?.message ?? err);
      const isChunkErr =
        /Loading chunk|Failed to fetch dynamically imported module|MIME type/i.test(msg);
      if (isChunkErr && !sessionStorage.getItem('casino_reload_attempted')) {
        sessionStorage.setItem('casino_reload_attempted', String(Date.now()));
        window.location.reload();
        // Return a never-resolving promise so React doesn't flash an error before reload
        return new Promise<T>(() => {});
      }
      throw err;
    })
  );
}

// Clear the reload guard after a successful mount (any async gap past init)
if (typeof window !== 'undefined') {
  setTimeout(() => sessionStorage.removeItem('casino_reload_attempted'), 10_000);
}

// P0-20 performance: lazy-load every other route so the initial bundle only
// contains the landing page + providers. Routes users rarely hit (/terms,
// /privacy) don't bloat the first-paint bundle.
const ChatPage = lazyWithReload(() => import('./pages/ChatPage'));
const Workspace = lazyWithReload(() => import('./pages/Workspace'));
const DataExplorer = lazyWithReload(() => import('./pages/DataExplorer'));
const Library = lazyWithReload(() => import('./pages/Library'));
const Automations = lazyWithReload(() => import('./pages/Automations'));
const ModelSettings = lazyWithReload(() => import('./pages/ModelSettings'));
const Settings = lazyWithReload(() => import('./pages/Settings'));
const SettingsFull = lazyWithReload(() => import('./pages/SettingsFull'));
const Plans = lazyWithReload(() => import('./pages/Plans'));
const Pricing = lazyWithReload(() => import('./pages/Pricing'));
const Infer = lazyWithReload(() => import('./pages/Infer'));
const Privacy = lazyWithReload(() => import('./pages/Privacy'));
const Terms = lazyWithReload(() => import('./pages/Terms'));
const GoogleCallback = lazyWithReload(() => import('./pages/GoogleCallback'));
const AuthBrokerPage = lazyWithReload(() => import('./pages/AuthBrokerPage'));
const AgentNetwork = lazyWithReload(() => import('./pages/AgentNetwork'));
// P0-192 FR5. Authed builder tooling — NOT the public /agents dashboard above.
const AgentFleet = lazyWithReload(() => import('./pages/AgentFleet'));
const McpDashboard = lazyWithReload(() => import('./pages/McpDashboard'));
const McpLanding = lazyWithReload(() => import('./pages/McpLanding'));
const Register = lazyWithReload(() => import('./pages/Register'));
const Complete = lazyWithReload(() => import('./pages/Complete'));

const Login = lazyWithReload(() => import('./pages/Login'));

/**
 * Root route: authenticated users land in the Workspace (the app's home),
 * logged-out visitors get the public creator Landing (describe → build → pay).
 * The Agent Network analytics dashboard now lives at /agents.
 *
 * P0-158: builder off → Casino is the MCP admin console. Anon visitors get
 * the MCP onboarding page; authed users go to the MCP dashboard.
 */
function RootRoute() {
  const ctx = useFlowstackOptional();
  if (ctx?.isInitialized && ctx.isAuthenticated) {
    return <Navigate to={WEB_BUILDER_ENABLED ? '/workspace' : '/mcp/dashboard'} replace />;
  }
  return WEB_BUILDER_ENABLED ? <Landing /> : <McpLanding />;
}

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-[var(--color-bg)]">
      <span className="w-6 h-6 rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent)] spin" />
    </div>
  );
}

export default function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <BrokerRescue />
      <AuthModal />
      <Routes>
        {/* Front door: creator Landing (builder on) or McpLanding (builder off). */}
        <Route path="/" element={<RootRoute />} />

        {/* Public agent-network analytics dashboard — moved off / so the
            creator Landing can be the front door. */}
        <Route path="/agents" element={<AgentNetwork />} />

        {/* Self-serve creator flow is now public: land → register → build.
            Landing stashes casino_pending_prompt; HomeView/ChatView auto-run it
            after auth. Free tier (60 credits/day) + card gate protect abuse. */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/complete" element={<Complete />} />
        {/* P0-157: MCP-first building — public onboarding for connecting
            Claude Code / Desktop / Cursor to the Casino MCP server. */}
        <Route path="/mcp" element={<McpLanding />} />

        {/* Auth-gated routes — chat is the default, workspace is for direct access.
            P0-158: builder off → /chat and /workspace redirect to the MCP
            dashboard; data/library/automations/settings/mcp stay reachable. */}
        <Route element={<WorkspaceLayout />}>
          {/* P0-93: /chat/:sessionId resumes a past session from the SessionsSidebar */}
          {WEB_BUILDER_ENABLED ? (
            <>
              <Route path="/chat/:sessionId" element={<ChatPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/workspace" element={<Workspace />} />
            </>
          ) : (
            <>
              <Route path="/chat/:sessionId" element={<Navigate to="/mcp/dashboard" replace />} />
              <Route path="/chat" element={<Navigate to="/mcp/dashboard" replace />} />
              <Route path="/workspace" element={<Navigate to="/mcp/dashboard" replace />} />
            </>
          )}
          <Route path="/workspace/data" element={<DataExplorer />} />
          <Route path="/workspace/library" element={<Library />} />
          <Route path="/workspace/automations" element={<Automations />} />
          {/* P0-192 FR5: the fleet delegation graph. Tenant-wide, so it lives
              here rather than inside one app's settings column. */}
          <Route path="/workspace/agents" element={<AgentFleet />} />
          <Route path="/settings" element={<SettingsFull />} />
          {/* P0-154: subscription plans — self-hides when the backend flag is off */}
          <Route path="/plans" element={<Plans />} />
          <Route path="/settings/models" element={<ModelSettings />} />
          <Route path="/settings/api-keys" element={<Settings />} />
          <Route path="/mcp/dashboard" element={<McpDashboard />} />
        </Route>

        {/* P0-60: Casino-as-IdP login broker for built apps.
            Built apps open this in a popup to delegate Privy login to Casino. */}
        <Route path="/auth/broker" element={<AuthBrokerPage />} />

        <Route path="/api/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/infer" element={<Infer />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        {/* Billing explainer — public; InfoTips across the app deep-link here */}
        <Route path="/pricing" element={<Pricing />} />

        {/* SPA fallback — unknown paths go to the root route. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
